/**
 * کارنامهٔ عضو, said two ways.
 *
 * The page was written for a manager reading about somebody else, so every
 * label is third person: «سرمایه‌اش», «ضمانت‌هایی که کرده», «نزد اوست». pwa#61
 * sends the member to the same page about themselves, where that voice is
 * wrong in the way that makes an app feel like it is talking behind your
 * back.
 *
 * Both voices live here rather than as `isSelf ? … : …` scattered through
 * four components, for two reasons: a reviewer can read the pair side by
 * side and catch a mismatched register, and a copy fix lands once instead of
 * in whichever branch someone remembered.
 *
 * Register is «شما», not «تو» — the app is formal throughout
 * («سرمایهٔ شما در صندوق‌ها» on the home page, «سرمایهٔ شما در این صندوق» in
 * the withdrawal wizard), and this page is about somebody's money.
 */

/** `about` = a manager reading about a member. `self` = the member. */
export type TRecordVoice = 'about' | 'self'

export interface IRecordCopy {
    pageTitle: string
    guarantorLabel: string
    recommenderLabel: string
    scoreTitle: string
    /** null → the explainer keeps naming the member, which is right for a manager */
    scoreSubject: string | null
    capitalTitle: string
    chartTitle: string
    /** what the (i) beside the chart says — see the note on ABOUT.chartHint */
    chartHint: string
    guaranteesTitle: string
    guaranteesNote: (count: string) => string
    /** the member is holding more of the fund's money than they put in */
    netOwing: (amount: string) => string
    /** the member has put in more than they have taken */
    netLending: (amount: string) => string
}

const ABOUT: IRecordCopy = {
    pageTitle: 'کارنامهٔ عضو',
    guarantorLabel: 'ضامن عضویتش',
    recommenderLabel: 'معرفش',
    scoreTitle: 'امتیاز عضو',
    scoreSubject: null,
    capitalTitle: 'سرمایه‌اش در صندوق',
    chartTitle: 'سرمایه‌اش در طول زمان',
    // Said as a rule the reader can act on, not as a property of the drawing.
    // It used to read «دقیقاً مساحت زیر همین نمودار است»: true, and the way
    // the backend actually computes it, but «مساحت زیر نمودار» is a sentence
    // from a maths class. It asks somebody checking a member's record to
    // picture an integral in order to learn something they already understand
    // — that money left in longer counts for more.
    chartHint:
        'هرچه پولِ بیشتری، مدتِ بیشتری در صندوق مانده باشد، جزء «تأمین سرمایه» در امتیاز عضو بالاتر می‌رود.',
    guaranteesTitle: 'ضمانت‌هایی که کرده',
    guaranteesNote: (count) =>
        `${count} نفر با ضمانت او عضو صندوق شده‌اند. رنگ نقطه، وضعیت پرداخت خودِ آن‌هاست.`,
    netOwing: (amount) =>
        `${amount} تومان بیشتر از سرمایهٔ خودش، از پول بقیهٔ اعضا نزد اوست.`,
    netLending: (amount) =>
        `${amount} تومان بیشتر از آنچه گرفته، در صندوق گذاشته است.`,
}

const SELF: IRecordCopy = {
    pageTitle: 'کارنامهٔ من',
    guarantorLabel: 'ضامن عضویت شما',
    recommenderLabel: 'معرف شما',
    scoreTitle: 'امتیاز شما',
    scoreSubject: 'شما',
    capitalTitle: 'سرمایهٔ شما در این صندوق',
    chartTitle: 'سرمایهٔ شما در طول زمان',
    chartHint:
        'هرچه پولِ بیشتری، مدتِ بیشتری در صندوق مانده باشد، جزء «تأمین سرمایه» در امتیاز شما بالاتر می‌رود.',
    guaranteesTitle: 'ضمانت‌هایی که کرده‌اید',
    guaranteesNote: (count) =>
        `${count} نفر با ضمانت شما عضو صندوق شده‌اند. رنگ نقطه، وضعیت پرداخت خودِ آن‌هاست.`,
    netOwing: (amount) =>
        `${amount} تومان بیشتر از سرمایهٔ خودتان، از پول بقیهٔ اعضا نزد شماست.`,
    netLending: (amount) =>
        `${amount} تومان بیشتر از آنچه گرفته‌اید، در صندوق گذاشته‌اید.`,
}

export const recordCopy = (voice: TRecordVoice): IRecordCopy =>
    voice === 'self' ? SELF : ABOUT
